'use client';

import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { createClient } from '@/lib/supabase/client';

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const supabase = createClient();
      
      // Call the Edge Function to send email
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      if (error) {
        console.error('Edge Function error details:', {
          message: error.message,
          context: error.context,
          name: error.name,
          fullError: error
        });
        
        // Try to get more details from the error context
        if (error.context) {
          console.error('Error context details:', JSON.stringify(error.context, null, 2));
        }
        
        // Log the response data which might contain the actual error
        if (data) {
          console.error('Edge Function response data:', JSON.stringify(data, null, 2));
        }
        
        throw error;
      }

      // Check if the response indicates an error
      if (data?.error) {
        console.error('Edge Function returned error:', data);
        throw new Error(data.error);
      }

      console.log('Email sent successfully:', data);
      setStatus('success');
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="bg-white rounded-2xl shadow-luxury-lg p-8 lg:p-12 border border-border/50">
      <h2 className="font-heading text-3xl font-bold text-primary mb-8 tracking-tight">
        {t('contact.form.title')}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block font-body text-base font-semibold text-foreground mb-3">
            {t('contact.form.name')}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={status === 'sending'}
            className="w-full px-5 py-4 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-luxury text-base disabled:opacity-50"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block font-body text-base font-semibold text-foreground mb-3">
            {t('contact.form.email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={status === 'sending'}
            className="w-full px-5 py-4 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-luxury text-base disabled:opacity-50"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block font-body text-base font-semibold text-foreground mb-3">
            {t('contact.form.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            disabled={status === 'sending'}
            className="w-full px-5 py-4 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-luxury text-base disabled:opacity-50"
          />
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block font-body text-base font-semibold text-foreground mb-3">
            {t('contact.form.subject')}
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            disabled={status === 'sending'}
            className="w-full px-5 py-4 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-luxury text-base disabled:opacity-50"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block font-body text-base font-semibold text-foreground mb-3">
            {t('contact.form.message')}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            disabled={status === 'sending'}
            className="w-full px-5 py-4 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-luxury resize-none text-base disabled:opacity-50"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full bg-accent text-accent-foreground py-4 px-8 rounded-xl font-body font-semibold hover:bg-accent/90 transition-luxury focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 shadow-luxury-sm hover:shadow-luxury-md hover:-translate-y-1 text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {status === 'sending' ? t('contact.form.sending') || 'Sending...' : t('contact.form.submit')}
        </button>

        {/* Status Messages */}
        {status === 'success' && (
          <div className="bg-success/10 border border-success/20 text-success px-5 py-4 rounded-xl">
            {t('contact.form.success')}
          </div>
        )}
        {status === 'error' && (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive px-5 py-4 rounded-xl">
            {t('contact.form.error')}
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;